import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collectCursorPages } from './pagination';
import { api, unwrap } from './client';
import type { components } from './schema';

export type DataObject = components['schemas']['DataObject'];
export type UploadSession = components['schemas']['UploadSession'];
export type SignedPutUrl = components['schemas']['SignedPutUrl'];
export type KindInfo = components['schemas']['Kind'];
export type RegisterObjectBody = components['schemas']['RegisterObjectRequest'];
type OpenSessionRequest = components['schemas']['OpenSessionRequest'];
export type OpenSessionBody = Omit<OpenSessionRequest, 'shape' | 'content_type'> &
  Partial<Pick<OpenSessionRequest, 'shape' | 'content_type'>>;
export type ObjectClientStatus =
  | 'waiting_for_data'
  | 'uploading'
  | 'checking_upload'
  | 'checking_data'
  | 'ready'
  | 'needs_attention'
  | 'deleted';

/** Above this the panel recommends the CLI (storage-and-uploads.md §5.1) but still allows the browser. */
export const UPLOAD_CLI_THRESHOLD_BYTES = 20 * 1024 ** 3;

export async function listObjects(projectId: string): Promise<DataObject[]> {
  return collectCursorPages(async (cursor) => unwrap(await api.GET('/api/projects/{project_id}/objects', { params: { path: { project_id: projectId }, query: { cursor } } })));
}
export async function registerObject(projectId: string, body: RegisterObjectBody): Promise<DataObject> {
  return unwrap(await api.POST('/api/projects/{project_id}/objects', { params: { path: { project_id: projectId } }, body }));
}
export async function getObject(id: string): Promise<DataObject> {
  return unwrap(await api.GET('/api/objects/{public_id}', { params: { path: { public_id: id } } }));
}
export async function renameObject(id: string, name: string): Promise<DataObject> {
  return unwrap(await api.PATCH('/api/objects/{public_id}', { params: { path: { public_id: id } }, body: { name } }));
}
/** Staff only: mark the object internal (no client lists or opens it) or take the mark off. */
export async function setObjectInternal(id: string, internal: boolean): Promise<DataObject> {
  return unwrap(await api.PATCH('/api/objects/{public_id}', { params: { path: { public_id: id } }, body: { internal } }));
}
export async function deleteObject(id: string, reason = ''): Promise<{ bytes: number; removed: boolean }> {
  return unwrap(
    await api.DELETE('/api/objects/{public_id}', { params: { path: { public_id: id }, query: { reason } } }),
  ) as { bytes: number; removed: boolean };
}
export async function deleteRaw(id: string, reason = ''): Promise<{ bytes: number }> {
  return unwrap(
    await api.POST('/api/objects/{public_id}/delete-raw', { params: { path: { public_id: id } }, body: { reason } }),
  ) as { bytes: number };
}
export type AttachUriBody = Omit<components['schemas']['AttachUriRequest'], 'dry_run'>;
/** Staff only: fill a pending object from an existing `gs://` blob or prefix, in place; `dry_run` only shows the result. */
export async function attachUri(id: string, body: AttachUriBody, dryRun = false): Promise<DataObject> {
  return unwrap(await api.POST('/api/objects/{public_id}/attach-uri', { params: { path: { public_id: id } }, body: { ...body, dry_run: dryRun } }));
}
export type RegisterReferencedBody = Omit<components['schemas']['RegisterReferencedRequest'], 'dry_run'>;
export type ReferencedResult = components['schemas']['ReferencedResult'];
/** Staff only: a new object from finished data in a bucket, optionally an analysis output; `dry_run` only shows the result. */
export async function registerReferenced(projectId: string, body: RegisterReferencedBody, dryRun = false): Promise<ReferencedResult> {
  return unwrap(await api.POST('/api/projects/{public_id}/referenced-objects', { params: { path: { public_id: projectId } }, body: { ...body, dry_run: dryRun } }));
}
export async function objectDependencies(id: string): Promise<{ kind: string; object?: string }[]> {
  return unwrap(await api.GET('/api/objects/{public_id}/dependencies', { params: { path: { public_id: id } } })) as {
    kind: string;
    object?: string;
  }[];
}
export async function openSession(objectId: string, body: OpenSessionBody): Promise<UploadSession> {
  return unwrap(await api.POST('/api/objects/{public_id}/sessions', { params: { path: { public_id: objectId } }, body: body as OpenSessionRequest }));
}
export async function listObjectSessions(objectId: string): Promise<UploadSession[]> {
  return unwrap(await api.GET('/api/objects/{public_id}/sessions', { params: { path: { public_id: objectId } } }));
}
export async function getSession(id: string): Promise<UploadSession> {
  return unwrap(await api.GET('/api/sessions/{public_id}', { params: { path: { public_id: id } } }));
}
export async function reportProgress(id: string, bytesUploaded: number): Promise<void> {
  unwrap(await api.POST('/api/sessions/{public_id}/progress', { params: { path: { public_id: id } }, body: { bytes_uploaded: bytesUploaded } }));
}
export async function completeSession(id: string): Promise<DataObject> {
  return unwrap(await api.POST('/api/sessions/{public_id}/complete', { params: { path: { public_id: id } } }));
}
export async function abandonSession(id: string): Promise<void> {
  unwrap(await api.POST('/api/sessions/{public_id}/abandon', { params: { path: { public_id: id } } }));
}
/** Signed PUT URLs for a batch of declared paths of a tree session; each URL is bound to its CRC. */
export async function issuePutUrls(sessionId: string, paths: { path: string; crc32c: string }[]): Promise<SignedPutUrl[]> {
  return unwrap(await api.POST('/api/sessions/{public_id}/urls', { params: { path: { public_id: sessionId } }, body: { paths } })).urls;
}
/** Declared paths already in the bucket at their declared size (resume skips them). */
export async function presentPaths(sessionId: string): Promise<{ path: string; size_bytes: number }[]> {
  return unwrap(await api.GET('/api/sessions/{public_id}/present', { params: { path: { public_id: sessionId } } })).present;
}
export async function listKinds(): Promise<KindInfo[]> {
  return unwrap(await api.GET('/api/kinds'));
}

export function useProjectObjects(projectId: string | undefined) {
  return useQuery({ queryKey: ['objects', projectId], queryFn: () => listObjects(projectId!), enabled: !!projectId });
}
export function useObject(id: string | undefined) {
  return useQuery({ queryKey: ['object', id], queryFn: () => getObject(id!), enabled: !!id });
}
export function useRenameObject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => renameObject(id, name),
    onSuccess: (o) => {
      qc.setQueryData(['object', id], o);
      void qc.invalidateQueries({ queryKey: ['objects'] });
      void qc.invalidateQueries({ queryKey: ['analysis'] });
      void qc.invalidateQueries({ queryKey: ['analyses'] });
    },
  });
}
/** Staff only. */
export function useObjectSessions(objectId: string | undefined) {
  return useQuery({ queryKey: ['object-sessions', objectId], queryFn: () => listObjectSessions(objectId!), enabled: !!objectId });
}
export function useKinds() {
  return useQuery({ queryKey: ['kinds'], queryFn: listKinds, staleTime: Infinity });
}

export type DrawnBoundaryBody = components['schemas']['DrawnBoundaryRequest'];

export async function createDrawnBoundary(projectId: string, body: DrawnBoundaryBody): Promise<DataObject> {
  return unwrap(await api.POST('/api/projects/{public_id}/drawn-boundaries', { params: { path: { public_id: projectId } }, body }));
}
export function useCreateDrawnBoundary(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DrawnBoundaryBody) => createDrawnBoundary(projectId, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['objects', projectId] }),
  });
}
